/**
 * Appels du poids (specs §6.6, archi §4.6).
 *
 * ⚠️ **Aucun calcul ne part ni n'arrive d'ici sous forme brute.** La moyenne
 * lissée, la tendance et l'écart au rythme visé sont établis par le serveur à
 * chaque lecture (§4.6 : « calculées à la volée, jamais stockées »). Le client ne
 * les recalcule nulle part — contrairement aux macros, où `lib/foodPreview.ts` et
 * `lib/recipePreview.ts` dupliquent le calcul serveur pour la mise à jour
 * optimiste. Ici ce serait à la fois inutile et nuisible : une pesée n'est pas
 * sur le chemin critique des 15 secondes (specs §4 la rangent en saisie
 * quotidienne unique), et une tendance recalculée à la va-vite côté client
 * pourrait diverger du chiffre officiel sans que rien ne le signale.
 *
 * ⚠️ **Toutes les mutations renvoient l'état complet**, plage comprise. Corriger
 * une pesée du mois dernier déplace sept points de moyenne lissée et la tendance :
 * un 204 obligerait à tout redemander juste après. La plage courante voyage donc
 * avec chaque écriture.
 */

import type {
  WeightEntryInput,
  WeightGoalDTO,
  WeightGoalInput,
  WeightRange,
  WeightResponse,
} from '../../../shared/types';
import { browserToday } from '../lib/dates';
import { apiFetch } from './client';

function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

/** Clé de cache de l'écran — une entrée par plage. */
export function weightKey(range: WeightRange) {
  return ['weight', range] as const;
}

/** Clé de l'historique des objectifs. */
export const WEIGHT_GOALS_KEY = ['weight-goals'] as const;

/**
 * Paramètres communs à toutes les routes de poids.
 *
 * ⚠️ `today` est la date du jour **dans le fuseau de l'appareil**. Sans elle, le
 * serveur retiendrait la sienne (`TZ`, §9) et proposerait d'enregistrer la pesée
 * d'hier dès qu'on se déplace de quelques fuseaux. Le journal fait de même
 * (`useDay`) : les deux écrans doivent appeler « aujourd'hui » le même jour.
 */
function params(range: WeightRange): string {
  return new URLSearchParams({ range, today: browserToday() }).toString();
}

/** Tout l'écran Progression en un appel : pesée du jour, résumé, objectif, série. */
export function fetchWeight(range: WeightRange): Promise<WeightResponse> {
  return apiFetch<WeightResponse>(`/api/weight?${params(range)}`);
}

/**
 * Saisie **ou** correction d'une pesée — la même opération (specs §6.6,
 * « historique consultable et corrigeable »).
 *
 * Idempotente par nature : la date est la clé. Pas de `clientId` ici, contrairement
 * aux créations du journal — il n'y a rien à dédoublonner.
 */
export function saveWeight(
  date: string,
  body: WeightEntryInput,
  range: WeightRange,
): Promise<WeightResponse> {
  return send<WeightResponse>('PUT', `/api/weight/${date}?${params(range)}`, body);
}

/** Retire une pesée — l'autre moitié de « corrigeable » (mauvaise date). */
export function deleteWeight(date: string, range: WeightRange): Promise<WeightResponse> {
  return send<WeightResponse>('DELETE', `/api/weight/${date}?${params(range)}`);
}

/** Historique des objectifs, le plus récent d'abord. */
export function fetchWeightGoals(): Promise<WeightGoalDTO[]> {
  return apiFetch<WeightGoalDTO[]>('/api/weight/goals');
}

/**
 * Nouvel objectif — **une ligne de plus**, jamais une modification (§4.6).
 * Les objectifs précédents restent en base et restent consultables.
 */
export function createWeightGoal(
  body: WeightGoalInput,
  range: WeightRange,
): Promise<WeightResponse> {
  return send<WeightResponse>('POST', `/api/weight/goals?${params(range)}`, body);
}

/** Retire une ligne d'objectif ; la précédente redevient l'objectif en vigueur. */
export function deleteWeightGoal(id: number, range: WeightRange): Promise<WeightResponse> {
  return send<WeightResponse>('DELETE', `/api/weight/goals/${String(id)}?${params(range)}`);
}
